import type {Ref} from 'react'
import {forwardRef, memo, useCallback, useEffect} from 'react'
import type {GridProps, SectionRenderedParams} from 'react-virtualized/dist/es/Grid'
import Grid from 'react-virtualized/dist/es/Grid'

import useGridSelection from '../../../../hooks/useGridSelection'
import type {BuildLogMessage} from '../BuildLog.types'
import {getNextExistIndex, getPrevExistIndex} from '../BuildLog.utils'

import {getTextForCoping} from './BuildLogMessages.utils'

import styles from './BuildLogMessages.css'

type Props = GridProps & {
  messages: ReadonlyArray<BuildLogMessage | null | undefined>
  scrollToRowByMessageId: (messageId: number) => void
  isAutodetectTimeZone: boolean
}

function GridSelectionWrapper(
  {
    messages,
    scrollToRowByMessageId,
    isAutodetectTimeZone,
    onSectionRendered: onSectionRenderedExternal,
    ...otherProps
  }: Props,
  ref: Ref<Grid>,
) {
  const [onSectionRenderedInternal, selectionState] = useGridSelection({
    rowClassName: styles.messageFocusWrapper,
    rowIdAttributeName: 'data-id',
    scrollToRow: scrollToRowByMessageId,
  })
  const hasSelection = selectionState != null

  const onCopy = useCallback(
    (event: ClipboardEvent) => {
      if (hasSelection && event.clipboardData != null) {
        const text = getTextForCoping({
          selectionState,
          messages,
          isAutodetectTimeZone,
        })
        if (text.length > 0) {
          event.clipboardData.setData('text/plain', text)
          event.preventDefault()
        }
      }
    },
    [hasSelection, isAutodetectTimeZone, messages, selectionState],
  )

  const onSectionRendered = useCallback(
    (data: SectionRenderedParams) => {
      const firstVisibleIndex = getNextExistIndex(messages, data.rowOverscanStartIndex - 1)
      const firstRowId = messages[firstVisibleIndex]?.id
      const lastVisibleIndex = getPrevExistIndex(messages, data.rowOverscanStopIndex + 1)
      const lastRowId = messages[lastVisibleIndex]?.id

      onSectionRenderedInternal(firstRowId, lastRowId)

      if (onSectionRenderedExternal != null) {
        onSectionRenderedExternal(data)
      }
    },
    [messages, onSectionRenderedExternal, onSectionRenderedInternal],
  )

  useEffect(() => {
    if (hasSelection) {
      document.addEventListener('copy', onCopy)
    }
    return () => document.removeEventListener('copy', onCopy)
  }, [onCopy, hasSelection])

  return <Grid {...otherProps} ref={ref} onSectionRendered={onSectionRendered} />
}

export default memo(forwardRef(GridSelectionWrapper))
